<template>
  <div class="rounded-tr-[2.5rem] pb-9 pl-5 pr-6 pt-4 sm:pr-9">
    <!-- Title -->
    <h3 class="mb-2 typo-heading-sm sm:typo-heading-lg">
      {{ pool.title }}
    </h3>

    <div class="flex max-sm:flex-col max-sm:gap-7 sm:grid sm:grid-cols-3">
      <!-- Chart -->
      <div class="col-span-1 bg-white/10">
        <div ref="chartContainer" class="flex size-full items-center">
          <div v-if="dataRows" class="aspect-square w-full">
            <ChartPie
              :chart-data="dataRows"
              :unit="unit"
              :unit-prefix="unitPrefix"
              :segment-config="segmentConfig"
              :show-labels="false"
            />
          </div>
        </div>
      </div>

      <div class="relative flex flex-col overflow-hidden sm:col-span-2 sm:ml-8">
        <div
          ref="gradient"
          class="pointer-events-none absolute inset-x-0 z-10 h-10 bg-gradient-to-b to-transparent opacity-0"
          :class="!colorTheme || colorTheme === 'blue' ? `from-theme-500` : `from-theme-200`"
        />
        <div ref="containerText">
          <!-- Legend -->
          <ul ref="legend" class="space-y-2">
            <li
              v-for="(row, index) in pool.legend"
              :key="index"
              class="flex w-full items-center justify-between gap-5"
            >
              <span :class="row.is_title ? 'mt-5 typo-body-xl-fat' : 'typo-body-xl'">{{ row.label }}</span>
              <span v-if="row.value" class="typo-body-xl-fat">
                {{ row.value }}
              </span>
            </li>
          </ul>

          <!-- Information -->
          <div ref="informationContainer" class="mt-auto border-b-2 pb-2 pt-8">
            <div class="flex items-center justify-between">
              <h4 class="typo-body-xl-fat">
                {{ $t('information') }}
              </h4>

              <button type="button" class="pl-4" @click="toggle">
                <Transition name="fade" mode="out-in">
                  <span v-if="!isOpen" class="flex items-center gap-2">
                    <span>{{ $t('open') }}</span>
                    <IconPlus class="size-5" />
                  </span>
                  <span v-else class="flex items-center gap-2">
                    <span>{{ $t('close') }}</span>
                    <IconMinus class="size-5" />
                  </span>
                </Transition>
              </button>
            </div>

            <div ref="informationCollapse" class="h-0 overflow-hidden">
              <p class="pt-2 typo-body-lg">
                {{ pool.information }}
              </p>
            </div>
          </div>

          <!-- Download Button -->
          <div v-if="pool.download_file" class="mt-7">
            <AppButton
              icon="download"
              download
              :to="pool.download_file.permalink"
              variant="outlined"
              target="_blank"
              size="sm"
            >
              {{ pool.download_button_text }}
            </AppButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import gsap from 'gsap'

import type { ComponentsMainOverviewPoolsFragment } from '#gql'

const chartContainer = ref<HTMLElement | null>(null)
const containerText = ref<HTMLElement | null>(null)
const gradient = ref<HTMLElement | null>(null)
const legend = ref<HTMLElement | null>(null)
const informationContainer = ref<HTMLElement | null>(null)
const informationCollapse = ref<HTMLElement | null>(null)

const props = defineProps<{
  pool: ComponentsMainOverviewPoolsFragment['pools'][0]
  unit: string | null
  unitPrefix: boolean
  segmentConfig: ComponentsMainOverviewPoolsFragment['segment_config']
  colorTheme?: string | null
}>()

const isOpen = ref(false)

const dataRows = computed(() => {
  return props.pool.chart_data
})

function toggle() {
  if (isOpen.value) {
    close()
    return
  }

  open()
}

let tl: gsap.core.Timeline
function open() {
  if (tl?.isActive()) return

  isOpen.value = true

  const chartContainerHeight = chartContainer.value?.clientHeight || 0

  const infoScrollHeight = informationCollapse.value?.scrollHeight || 0
  // Get current height of legend and information
  const legendHeight = legend.value?.clientHeight || 0
  const spaceAbove = Math.min(infoScrollHeight, legendHeight)

  tl = gsap.timeline({
    defaults: {
      duration: 0.4,
      ease: 'power1.inOut',
    },
  })
  tl.to(chartContainer.value, {
    height: `${chartContainerHeight}px`,
  })
    .to(informationCollapse.value, {
      height: 'auto',
    }, '<')
    .to(gradient.value, {
      opacity: 1,
    }, '<')
    .to(containerText.value, {
      marginTop: `-${spaceAbove}px`,
    }, '<')
}

function close() {
  if (tl?.isActive()) return
  tl.timeScale(1.25)
  tl.reverse().then(() => {
    isOpen.value = false
  })
}

onUnmounted(() => {
  tl?.kill()
})
</script>
